import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3 } from "lucide-react";
import { PageHead, Wrap, H2, P, Prose, Impact, Callout, Disclaimer } from "@/components/Content";
import { Seo, breadcrumbSchema } from "@/components/Seo";

export default function Results() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title="Results & Methodology | CardScience HQ"
        description="$3.9M+ in documented PSA 10 value since 2021. We do not claim our service caused those grades. Learn our honest results methodology — no guaranteed grades, no forced results."
        path="/results"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Results", path: "/results" }])}
      />
      <PageHead eyebrow="Results" title={<>THE GRADER MAKES THE CALL.<br />WE MAKE SURE YOUR CARD SHOWS UP READY.</>} tone="blue" />

      <Wrap className="py-16 space-y-14">
        {/* Feature stat */}
        <div className="rounded-3xl holo-border holo-glow p-8 sm:p-12 text-center">
          <BarChart3 size={30} className="text-holo mx-auto mb-4" />
          <p className="h-display text-3xl sm:text-5xl text-holo leading-tight">$3.9 MILLION+</p>
          <p className="h-display text-lg sm:text-2xl text-chrome mt-2">IN DOCUMENTED PSA 10 VALUE SINCE 2021</p>
        </div>

        <Prose>
          <P>
            Since 2021, more than $3.9 million in documented collectible value has been associated with cards
            that passed through our process and subsequently received PSA 10 grades.
          </P>
          <P className="text-white font-semibold">CardScience HQ does not claim that our service caused those grades.</P>
          <P>The grading decision belongs exclusively to PSA.</P>
          <P>Our role is to help eligible cards present at their best before they are evaluated.</P>
        </Prose>

        {/* Not gradeable */}
        <Callout>
          <H2>WE DO NOT GUARANTEE GRADES.<br />WE DO NOT GUARANTEE RESULTS.</H2>
          <Prose className="mt-6">
            <P>Not every cleaned card is gradeable.</P>
            <P>Not every card should be graded.</P>
            <P>
              A professionally presented card may still contain scratches, dents, creases, print defects,
              centering issues, surface damage, edge wear, corner wear, staining, manufacturing defects or
              other issues that affect grading.
            </P>
            <P>Cleaning cannot erase every defect.</P>
            <P>Preparation cannot turn a damaged card into a perfect card.</P>
            <P>CardScience HQ does not determine grades.</P>
            <P>Independent grading companies determine their own grades under their own standards and policies.</P>
            <P>Those standards may change. Their decisions are theirs alone.</P>
            <P>If you are a lover of the hobby and want your collectible to present at its absolute best, CardScience HQ is built for you.</P>
            <P>Some customers submit cards because they plan to grade them. Some submit because they want to preserve their collection. Some submit rare cards. Some submit inexpensive cards. Some submit childhood cards that will never be sold.</P>
            <P>The card does not need to be expensive to matter.</P>
          </Prose>
          <Impact tone="holo">THE CARD MATTERS BECAUSE IT MATTERS TO YOU.</Impact>
        </Callout>

        <Disclaimer>
          <p>PSA determines all grades independently.</p>
          <p>No grade is guaranteed.</p>
          <p>Past results do not guarantee future results.</p>
          <p>
            CardScience HQ is not affiliated with, endorsed by, sponsored by or approved by PSA, Professional
            Sports Authenticator, CGC, Beckett, SGC or any other independent grading company unless explicitly
            stated otherwise.
          </p>
        </Disclaimer>

        <div className="flex flex-wrap gap-3">
          <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">SUBMIT A COLLECTIBLE <ArrowRight size={16} /></Link>
          <Link to="/how-it-works" className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">HOW IT WORKS</Link>
        </div>
      </Wrap>
    </div>
  );
}
